import * as React from 'react';
import * as I18n from 'i18n-js';
import { get } from 'lodash';
import { selectedPath } from '../facet_utils';

import { Filter, WidgetType, getRangeUnsetParams } from './search_filters';
import FilterLink from './filter_link';
import { WithUserContext, IUserContext, IUser } from './user_context_provider';

interface IProps {
  filters: Filter[];
}

interface IPill {
  key: string;
  terms: {
    name: string;
    params: PlainObject;
  }[];
}

const WIDGET_CONFIGS: { [key: string]: (filter: Filter, user?: IUser) => IPill[] } = {
  [WidgetType.RANGE]: rangePillConfig,
  [WidgetType.NESTED_SELECT]: nestedPillConfig,
  [WidgetType.CHECKBOX]: defaultPillConfig,
  [WidgetType.REGION_SELECT]: regionPillConfig,
  [WidgetType.RADIO]: defaultPillConfig,
  [WidgetType.TEXT]: defaultPillConfig,
  [WidgetType.SORT]: _filter => [],
};

function regionPillConfig(filter: Filter, user: IUser): IPill[] {
  const opts = filter.options.filter(
    opt => opt.selected &&
      !opt.all &&
      opt.optionValue !== user.shippingRegionCode,
  );

  return opts.map((opt) => {
    return {
      key: opt.paramName,
      terms: [
        {
          name: I18n.t(`commons.pills.${opt.paramName}`, { region: opt.name }),
          params: { [opt.paramName]: opt.unsetValues },
        },
      ],
    };
  });
}

function nestedPillConfig(filter: Filter, _user?: IUser): IPill[] {
  const terms = selectedPath(filter);
  if (terms.length === 0) {
    return [];
  }
  if (terms.length === 1 && terms[0].all) {
    return [];
  }

  const termOptions = terms.map((opt) => {
    return {
      name: opt.name,
      params: { [opt.paramName]: opt.unsetValues },
    };
  });

  return [
    {
      key: terms[0].paramName,
      terms: termOptions,
    },
  ];
}

function rangePillConfig(filter: Filter, _user?: IUser): IPill[] {
  const displayOption = filter.options[2];
  if (!displayOption || !displayOption.selected) { return []; }

  return [
    {
      key: filter.options[0].paramName,
      terms: [
        {
          name: displayOption.name,
          params: getRangeUnsetParams(filter),
        },
      ],
    },
  ];
}

function defaultPillConfig(filter: Filter, _user?: IUser): IPill[] {
  const selectedOpts = filter.options.filter(opt => opt.selected && !opt.all);

  return selectedOpts.map((opt) => {
    return {
      key: `${opt.paramName}${opt.optionValue}`,
      terms: [
        {
          name: opt.name,
          params: { [opt.paramName]: opt.unsetValues },
        },
      ],
    };
  });
}

function filtersToPills(filters: Filter[], user: IUser): IPill[] {
  return filters.reduce(
    (acc, filter) => {
      const configFn = get(WIDGET_CONFIGS, filter.widgetType, defaultPillConfig);
      return acc.concat(configFn(filter, user));
    },
    [],
  );
}

function Pill(props: IPill) {
  return (
    <div className="search-pill">
      {props.terms.map((opt, idx) => {
        return (
          <div className="search-pill__term" key={idx}>
            {opt.name}
            <FilterLink
              params={opt.params}
              className="search-pill__remove"
              rel="nofollow"
            />
          </div>
        );
      })}
    </div>
  );
}

@WithUserContext
export default class RQLPills extends React.Component<IProps, null> {
  context: IUserContext;

  pills(): IPill[] {
    return filtersToPills(this.props.filters, this.context.user);
  }

  render() {
    const pills = this.pills();
    if (pills.length === 0) {
      return null;
    }

    return (
      <ul className="search-pills">
        <li className="search-pills__label">
          {I18n.t('commons.pills.filteredBy')}
        </li>
        {pills.map(pill => <Pill terms={pill.terms} key={pill.key} />)}
      </ul>
    );
  }
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/components/rql_pills.tsx